import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toToneMarks } from "@xinghai/shared";
import { Button, Card, EmptyState, Icon, Select, Skeleton, Slider } from "@xinghai/ui";
import { ApiError, annotateApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { AudioPanel } from "./components/AudioPanel";
import {
  FINAL_OPTIONS,
  INITIAL_OPTIONS,
  MARK_LABEL,
  TONE_OPTIONS,
  conflictValueLabel,
  dimensionKind,
  dimensionLabel,
} from "./labels";
import type { RereviewItem } from "./types";
import { MARK_ORDER } from "./labels";

/** Use this expert's own value as the starting point for the confirm control. */
function initialValue(item: RereviewItem): number | string | null {
  if (item.my_value !== null && item.my_value !== undefined) return item.my_value;
  return dimensionKind(item.dimension) === "score" ? 1 : null;
}

/**
 * docs/ROADMAP.md A4 — 复评队列.
 *
 * §5.4 「讨论」档 (0.10 < Δ ≤ 0.25): the peer's number is revealed **for these
 * dimensions only**, without a name, so both experts can reconsider before the
 * merge is recomputed. Anything beyond 0.25 never appears here — it goes to
 * arbitration instead.
 */
export function RereviewPage() {
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: qk.annotateRereview, queryFn: () => annotateApi.rereview() });
  const items = useMemo(() => q.data?.items ?? [], [q.data]);

  const [values, setValues] = useState<Record<string, number | string | null>>({});
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [pending, setPending] = useState<string | null>(null);

  const confirmItem = useMutation({
    mutationFn: (vars: { item: RereviewItem; value: number | string | null }) => {
      setPending(vars.item.id);
      return annotateApi.confirmRereview(vars.item.id, vars.value);
    },
    onSuccess: (_res, vars) => {
      setPending(null);
      setDone((d) => ({ ...d, [vars.item.id]: true }));
      void queryClient.invalidateQueries({ queryKey: qk.annotateRereview });
    },
    onError: (err) => {
      setPending(null);
      console.error(err instanceof ApiError ? err.message : err);
    },
  });

  // One block per recording, so the audio is loaded once for all its rows.
  const groups = useMemo(() => {
    const map = new Map<string, RereviewItem[]>();
    for (const it of items) {
      const list = map.get(it.recording_id) ?? [];
      list.push(it);
      map.set(it.recording_id, list);
    }
    return [...map.entries()];
  }, [items]);

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={100} />
        <Skeleton variant="rect" height={240} />
      </div>
    );
  }

  const control = (item: RereviewItem) => {
    const value = item.id in values ? values[item.id]! : initialValue(item);
    const set = (v: number | string | null) => setValues((m) => ({ ...m, [item.id]: v }));
    const kind = dimensionKind(item.dimension);

    if (kind === "score") {
      return (
        <Slider
          value={typeof value === "number" ? value : null}
          label={`${dimensionLabel(item.dimension)} 我的复评值`}
          step={0.05}
          onChange={set}
        />
      );
    }
    if (kind === "mark") {
      return (
        <Select
          aria-label={`${dimensionLabel(item.dimension)} 我的复评值`}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => set(e.target.value || null)}
        >
          <option value="">未选择</option>
          {MARK_ORDER.map((m) => (
            <option key={m} value={m}>
              {MARK_LABEL[m]}
            </option>
          ))}
        </Select>
      );
    }
    if (kind === "tone") {
      return (
        <Select
          aria-label={`${dimensionLabel(item.dimension)} 我的复评值`}
          value={typeof value === "number" ? String(value) : ""}
          onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))}
        >
          <option value="">未选择</option>
          {TONE_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      );
    }
    return (
      <Select
        aria-label={`${dimensionLabel(item.dimension)} 我的复评值`}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => set(e.target.value || null)}
      >
        <option value="">未选择</option>
        <optgroup label="声母">
          {INITIAL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="韵母">
          {FINAL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      </Select>
    );
  };

  return (
    <div className="xz-container xz-stack xz-rereview">
      <header className="xz-queue__head">
        <h1 className="gz-type-heading">复评</h1>
        <dl className="xz-queue__counts">
          <div>
            <dt>待复评</dt>
            <dd className="gz-type-score">{items.length}</dd>
          </div>
        </dl>
      </header>

      <p className="gz-notice" role="note">
        <Icon id="ui-info" size={20} />
        这些是两位专家差距在 0.10–0.25
        之间的「讨论」档。为了复评，这一档会显示对方的数值（不显示姓名或其它维度）。 差距超过 0.25
        的不会出现在这里，直接进入仲裁。
      </p>

      {items.length > 0 && (
        <div className="xz-rereview__bulk">
          <Button
            variant="primary"
            loading={confirmItem.isPending}
            onClick={() => {
              for (const it of items) {
                if (done[it.id]) continue;
                confirmItem.mutate({
                  item: it,
                  value: it.id in values ? values[it.id]! : initialValue(it),
                });
              }
            }}
          >
            全部确认
          </Button>
          <span className="gz-caption">
            保持当前数值，一次性确认剩下的 {items.filter((i) => !done[i.id]).length} 条。
          </span>
        </div>
      )}

      {groups.length === 0 ? (
        <EmptyState
          icon={<Icon id="ui-check-circle" size={24} />}
          title="没有待复评的条目"
          description="两份判断都提交后，落在讨论档的维度会出现在这里。"
          action={
            <Link className="gz-button gz-button--primary" to="/a">
              回到标注队列
            </Link>
          }
        />
      ) : (
        groups.map(([recordingId, rows]) => (
          <Card
            key={recordingId}
            padding="md"
            title={<span className="gz-type-data">{recordingId}</span>}
            subtitle={rows[0]?.material_title ?? undefined}
          >
            <AudioPanel
              src={rows[0]?.audio_url ?? ""}
              compact
              keyboard={false}
              label={`录音 ${recordingId}`}
            />
            <ul className="xz-arb__rows">
              {rows.map((item) => (
                <li key={item.id} className="xz-arb__row">
                  <div className="xz-arb__meta">
                    <span className="xz-arb__glyph">
                      {item.char && <span className="xz-arb__char">{item.char}</span>}
                      {item.pinyin_locked && (
                        <span className="xz-arb__pinyin">{toToneMarks(item.pinyin_locked)}</span>
                      )}
                      <span className="xz-arb__dim">{dimensionLabel(item.dimension)}</span>
                    </span>
                    <span className="xz-arb__ab">
                      <span>
                        我的值 <b>{conflictValueLabel(item.dimension, item.my_value)}</b>
                      </span>
                      <span>
                        对方值 <b>{conflictValueLabel(item.dimension, item.other_value)}</b>
                      </span>
                    </span>
                  </div>

                  {done[item.id] ? (
                    <p className="xz-arb__done" role="status">
                      <Icon id="ui-check-circle" size={20} /> 已复评
                    </p>
                  ) : (
                    <div className="xz-rereview__control">
                      {control(item)}
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={pending === item.id && confirmItem.isPending}
                        onClick={() => confirmItem.mutate({ item, value: item.my_value })}
                      >
                        维持
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        loading={pending === item.id && confirmItem.isPending}
                        onClick={() =>
                          confirmItem.mutate({
                            item,
                            value: item.id in values ? values[item.id]! : initialValue(item),
                          })
                        }
                      >
                        确认
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        ))
      )}
    </div>
  );
}
